import servicePages from '@/lib/service-pages.json';
import {Section} from '@/components/Marketing';
import React from "react";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";
import { TRAINING_FAQ } from "../lib/faq-content";
import FAQAccordion from "../components/FAQAccordion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  ShieldCheck,
  HardHat,
  Users,
  CheckCircle2,
  Award,
  AlertTriangle,
  Quote,
  Flame,
  ClipboardCheck,
  Plane,
} from "lucide-react";
import {
  OSHA_OFFERINGS,
  TRAINING_PROGRAMS,
  TRAINING_AUTHORITY,
  MEDIA,
} from "@/lib/site-data";

const COURSE_ROUTES=Object.fromEntries(Object.entries(servicePages).filter(([,page])=>page.group==='training').map(([route,page])=>[page.programId,route]));

export default function Training() {
  useDocumentMeta(SEO_ROUTES["/training"]);
  return (
    <div data-testid="training-page" className="w-full">
      <div className="forge-site"><Section eyebrow="Available to Schedule Now" title="Plan the Training Around Your Crew." body="Start with a consultation about topics, crew size, language, location, and delivery requirements. We agree on pricing and dates, then reserve the training on the calendar."/></div>
      {/* ============ HERO ============ */}
      <section className="relative min-h-[78vh] flex items-center border-b border-zinc-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${'/media/forge_safety_standup.jpg'}')`,
            backgroundPosition: "55% 35%",
            filter: "brightness(0.55) contrast(1.1) saturate(1.0)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent" />
        <div className="absolute -bottom-32 -left-32 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(255,107,26,0.20)_0%,transparent_65%)] pointer-events-none" />

        {/* Embers */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {[
            { l: "10%", b: "8%", d: 0, dr: "20px" },
            { l: "32%", b: "5%", d: 1.5, dr: "-30px" },
            { l: "55%", b: "12%", d: 2.7, dr: "40px" },
            { l: "78%", b: "8%", d: 1.0, dr: "-25px" },
            { l: "92%", b: "14%", d: 3.6, dr: "30px" },
          ].map((e, i) => (
            <span
              key={i}
              className="ember"
              style={{
                left: e.l,
                bottom: e.b,
                animationDelay: `${e.d}s`,
                "--drift": e.dr,
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8 py-24">
          <div className="col-span-12 lg:col-span-10 flex flex-col gap-7">
            <div className="flex items-center gap-4">
              <span className="inline-block w-2 h-2 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.45em] text-[11px] text-[#ff6b1a]">
                OSHA Outreach Training
              </span>
            </div>
            <h1
              data-testid="training-title"
              className="font-display uppercase text-[clamp(2.6rem,8vw,7rem)] leading-[0.85] font-black tracking-tight text-balance"
            >
              Field-Led Safety
              <br />
              <span className="metallic-text">Crews Actually Respect.</span>
            </h1>
            <p className="text-zinc-200 text-lg md:text-2xl leading-[1.4] max-w-3xl font-light">
              OSHA 10 &amp; 30-Hour Construction Outreach and practical safety
              training from people who have run heavy civil work. Florida based,
              with onsite, yard and training-room delivery by arrangement.
              Jobsite hazards, clear responsibilities and training your crews
              can connect to the work in front of them.
            </p>
            <div className="flex flex-wrap items-center gap-6 pt-3">
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <ShieldCheck size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.2em] text-[11px]">
                  OSHA-Authorized Outreach
                </span>
              </div>
              <span className="h-3 w-px bg-zinc-700" />
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <HardHat size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.2em] text-[11px]">
                  Onsite · In-Yard · Office
                </span>
              </div>
              <span className="h-3 w-px bg-zinc-700" />
              <div className="flex items-center gap-2 text-zinc-400 text-sm">
                <Users size={14} className="text-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.2em] text-[11px]">
                  Heavy Civil Focus
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 pt-5">
              <Link
                to="/contact?interest=Safety%20%26%20workforce%20training"
                data-testid="training-hero-cta-primary"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-[#ff8533] transition-all shadow-[0_0_50px_rgba(255,107,26,0.4)] hover:shadow-[0_0_80px_rgba(255,107,26,0.6)]"
              >
                Schedule Onsite Training
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
              <Link
                to="#osha-offerings"
                data-testid="training-hero-cta-secondary"
                className="inline-flex items-center gap-3 px-8 py-4 border border-zinc-500 text-white font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-white/5 hover:border-[#ff6b1a] transition-all"
              >
                See OSHA 10 & 30 <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OSHA OFFERINGS ============ */}
      <section
        id="osha-offerings"
        className="py-24 border-b border-zinc-900 bg-[#070708]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="flex flex-col gap-4 max-w-3xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                  Outreach Training
                </span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl font-black leading-[0.92] tracking-tight">
                OSHA 10 & 30-Hour
                <br />
                <span className="metallic-text">Outreach Training.</span>
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm leading-relaxed">
              Both 10-Hour and 30-Hour OSHA-authorized Outreach Training
              programs delivered with a heavy civil and highway construction
              lens — not generic warehouse curriculum.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            {OSHA_OFFERINGS.map((o, idx) => (
              <div
                key={o.code}
                data-testid={`osha-offering-${o.code.replace(/\s+/g, "-").toLowerCase()}`}
                className="ops-card group bg-[#141417] p-8 md:p-10 flex flex-col gap-6 border-t-2 border-t-transparent hover:border-t-[#ff6b1a] transition-all min-h-[460px]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-5xl md:text-6xl font-black tracking-tight metallic-text">
                      {o.code}
                    </span>
                  </div>
                  <span className="font-display text-xs tracking-[0.3em] uppercase text-zinc-600">
                    Course {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-display uppercase text-2xl md:text-3xl font-bold tracking-tight leading-tight">
                  {o.title}
                </h3>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#ff6b1a]/80 border-l-2 border-[#ff6b1a]/40 pl-3">
                  Audience: {o.audience}
                </div>
                <p className="text-zinc-300 text-sm md:text-base leading-relaxed">
                  {o.body}
                </p>
                <ul className="mt-auto pt-4 border-t border-zinc-900 flex flex-col gap-2.5">
                  {o.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-[13px] text-zinc-300"
                    >
                      <CheckCircle2
                        size={14}
                        className="mt-[3px] text-[#ff6b1a] flex-shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={COURSE_ROUTES['osha-'+o.code.split(' ')[1]]}
                  data-testid={`osha-offering-cta-${o.code.replace(/\s+/g, "-").toLowerCase()}`}
                  className="mt-2 inline-flex items-center gap-2 self-start font-display uppercase tracking-[0.18em] text-[12px] font-bold text-[#ff6b1a] hover:text-[#ff8533] transition-colors"
                >
                  Schedule {o.code} Training
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AUTHORITY BAND ============ */}
      <section className="py-24 border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(255,107,26,0.10)_0%,transparent_65%)] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-12 items-start">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                Why field crews respect it
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl md:text-5xl font-black leading-[0.92] tracking-tight">
              Real-World Authority.
              <br />
              <span className="metallic-text">Not Classroom Theory.</span>
            </h2>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-md">
              Heavy civil safety isn't a generic OSHA module. The hazards,
              the equipment, the crew dynamics — they all run different in
              the dirt than they do in a warehouse. Our training reflects
              that.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <ul
              data-testid="training-authority-grid"
              className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900"
            >
              {TRAINING_AUTHORITY.map((a, i) => (
                <li
                  key={a.label}
                  className="ops-card bg-[#141417] p-7 flex flex-col gap-3 min-h-[180px]"
                >
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 border border-[#ff6b1a]/40 bg-[#ff6b1a]/5 flex items-center justify-center">
                      <Award size={16} className="text-[#ff6b1a]" />
                    </div>
                    <span className="font-display text-xs tracking-[0.3em] uppercase text-zinc-600">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display uppercase text-lg font-bold tracking-tight">
                    {a.label}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {a.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ SPECIALIZED PROGRAMS — 12-card grid ============ */}
      <section
        data-testid="training-programs-grid"
        className="reveal py-24 bg-[#070708] border-b border-zinc-900 relative overflow-hidden"
      >
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[radial-gradient(circle,rgba(255,107,26,0.10)_0%,transparent_65%)] pointer-events-none" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div className="flex flex-col gap-4 max-w-3xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-[#ff6b1a]" />
                <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                  Specialized Programs
                </span>
              </div>
              <h2 className="font-display uppercase text-4xl md:text-6xl font-black leading-[0.92] tracking-tight">
                Heavy Civil Safety —
                <br />
                <span className="text-zinc-500">Discipline by Discipline.</span>
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm leading-relaxed">
              Twelve standalone training disciplines, each engineered for the
              hazards and equipment that actually show up on heavy civil and
              highway work. Stack what you need; we deliver onsite or in-yard.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-px bg-zinc-900 border border-zinc-900">
            {(() => {
              const items = TRAINING_PROGRAMS.filter((p) => !p.isFeatured);
              const trailing = items.length % 3; // 0 = full last row, 1 = single, 2 = pair
              return items.map((p, idx) => {
                const isLast = idx === items.length - 1;
                const isSecondLast = idx === items.length - 2;
                // Center the trailing row at lg+ when it's not full
                const lgCenterClass =
                  trailing === 2 && isSecondLast
                    ? "lg:col-start-2" // pair → cards land at cols 2-3 and 4-5
                    : trailing === 1 && isLast
                    ? "lg:col-start-3" // single → card lands at cols 3-4 (centered)
                    : "";
                return (
              <div
                key={p.id}
                data-testid={`training-program-${p.id}`}
                className={`ops-card group bg-[#141417] p-7 flex flex-col gap-4 min-h-[340px] border-t-2 border-t-transparent hover:border-t-[#ff6b1a] transition-all lg:col-span-2 ${
                  isLast && trailing === 1 ? "md:col-span-2" : ""
                } ${lgCenterClass}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-600 leading-snug">
                    {p.kicker}
                  </span>
                  <span className="font-display uppercase tracking-[0.18em] text-[9px] text-[#ff6b1a] border border-[#ff6b1a]/40 bg-black/40 px-2 py-1 whitespace-nowrap">
                    {p.focus}
                  </span>
                </div>
                <h3 className="font-display uppercase text-xl md:text-2xl font-bold tracking-tight leading-tight text-white">
                  <Link to={COURSE_ROUTES[p.id]} className="hover:text-[#ff6b1a] hover:underline">{p.title}</Link>
                </h3>
                <ul className="flex flex-col gap-1.5 mt-1">
                  {p.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[12.5px] text-zinc-300 leading-snug"
                    >
                      <CheckCircle2
                        size={12}
                        className="mt-[3px] text-[#ff6b1a] flex-shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ============ FEATURED — AIRFIELD CONSTRUCTION SAFETY ============ */}
      <section
        data-testid="training-airfield-featured"
        className="reveal relative py-24 md:py-28 border-b border-zinc-900 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('${MEDIA.opAirfieldDrain}')`,
            backgroundPosition: "50% 60%",
            filter: "brightness(0.65) contrast(1.1) saturate(1.0)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/85 to-black/95" />
        <div className="absolute -bottom-32 right-0 w-[700px] h-[700px] bg-[radial-gradient(circle,rgba(255,107,26,0.15)_0%,transparent_60%)] pointer-events-none" />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <Plane size={14} className="text-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.45em] text-[10px] text-[#ff6b1a]">
                Niche Authority
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl md:text-6xl font-black leading-[0.92] tracking-tight">
              Airfield
              <br />
              <span className="metallic-text">Construction Safety.</span>
            </h2>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-md">
              Airfield work is a different animal — live taxiways, FOD
              discipline, FAA escort procedures, and zero-tolerance for
              error. We deliver dedicated safety training built around
              construction crews working on active aviation operations
              areas (AOA).
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {[
                "FAA Aligned",
                "Live AOA Operations",
                "FOD Discipline",
                "Runway Safety Areas",
              ].map((t) => (
                <span
                  key={t}
                  className="font-display uppercase tracking-[0.18em] text-[10px] text-[#ff6b1a] border border-[#ff6b1a]/40 bg-black/40 px-2.5 py-1"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div
              data-testid="training-airfield-card"
              className="ops-featured rounded-none bg-[#0c0c0e] border border-[#ff6b1a]/30 p-7 md:p-8 flex flex-col gap-5"
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                  Specialized Program 12
                </span>
                <span className="font-display uppercase tracking-[0.2em] text-[10px] text-[#ff6b1a] border border-[#ff6b1a]/50 bg-[#ff6b1a]/10 px-2.5 py-1">
                  FAA / Airfield
                </span>
              </div>
              <h3 className="font-display uppercase text-2xl md:text-3xl font-bold tracking-tight text-white">
                The Training That Keeps Your Crews
                <br className="hidden md:inline" />
                <span className="text-[#ff6b1a]"> Off the Runway News.</span>
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {TRAINING_PROGRAMS.find((p) => p.id === "airfield").bullets.map(
                  (b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[13px] text-zinc-200 leading-snug"
                    >
                      <CheckCircle2
                        size={13}
                        className="mt-[3px] text-[#ff6b1a] flex-shrink-0"
                      />
                      <span>{b}</span>
                    </li>
                  )
                )}
              </ul>
              <Link
                to={COURSE_ROUTES.airfield}
                data-testid="training-airfield-cta"
                className="mt-2 inline-flex self-start items-center gap-2 font-display uppercase tracking-[0.18em] text-[12px] font-bold text-[#ff6b1a] hover:text-[#ff8533] transition-colors"
              >
                Schedule Airfield Training
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STAND-DOWN PULL QUOTE ============ */}
      <section className="relative py-28 overflow-hidden border-b border-zinc-900">
<div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/85 to-black/95" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(255,107,26,0.18)_0%,transparent_60%)] pointer-events-none" />
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10 flex flex-col items-center text-center gap-8">
          <Quote className="text-[#ff6b1a]/40" size={56} strokeWidth={1.2} />
          <p className="font-display text-3xl md:text-5xl font-black leading-[1.1] tracking-tight text-balance max-w-4xl">
            Crews don't get hurt because of{" "}
            <span className="text-zinc-500 line-through decoration-[#ff6b1a]/60 decoration-2">
              bad regulations
            </span>
            . <span className="metallic-text">They get hurt</span> because the
            training never <span className="text-[#ff6b1a]">left the binder.</span>
          </p>
          <span className="font-display uppercase tracking-[0.4em] text-xs text-zinc-500">
            — ForgedOps Safety Leadership
          </span>
        </div>
      </section>

      {/* ============ DELIVERY MODES ============ */}
      <section className="py-24 border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a]">
                Delivery
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl md:text-5xl font-black leading-[0.92] tracking-tight">
              We Come
              <br />
              <span className="metallic-text">Where You Run Work.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed max-w-md">
              Send the course or hazards you need covered, crew count,
              preferred language, location and date options. We confirm the
              instructor, facilities, travel, pricing and course requirements
              before reserving the dates.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
              {[
                {
                  label: "Onsite Jobsite",
                  body: "Train at the active project — between shifts, in the lull, or during a planned stand-down.",
                  icon: HardHat,
                },
                {
                  label: "Equipment Yard",
                  body: "Yard-based training keeps the crew tied to the iron — hands-on context they'll remember.",
                  icon: ClipboardCheck,
                },
                {
                  label: "Your Office / Training Room",
                  body: "When weather, schedule, or scope require a controlled space, we set up where you sit.",
                  icon: Users,
                },
              ].map((d, i) => {
                const Icon = d.icon;
                return (
                  <li
                    key={d.label}
                    data-testid={`training-delivery-${d.label.toLowerCase().replace(/\s+|\//g, "-").replace(/-+/g, "-")}`}
                    className="ops-card bg-[#141417] p-7 flex flex-col gap-3 min-h-[220px] border-t-2 border-t-transparent hover:border-t-[#ff6b1a] transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 border border-[#ff6b1a]/40 bg-[#ff6b1a]/5 flex items-center justify-center">
                        <Icon size={16} className="text-[#ff6b1a]" />
                      </div>
                      <span className="font-display text-xs tracking-[0.3em] uppercase text-zinc-600">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-display uppercase text-lg font-bold tracking-tight">
                      {d.label}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      {d.body}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ — visible Q&As (FAQPage schema is already injected above) */}
      <FAQAccordion
        kicker="Common Questions From Heavy Civil Operators"
        title="Field-Tested Answers."
        items={TRAINING_FAQ}
        testidPrefix="training-faq"
      />

      {/* ============ CTA ============ */}
      <section className="relative py-28 md:py-36 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('${MEDIA.opNightCranes}')`,
            filter: "brightness(0.6) contrast(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/55" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(255,107,26,0.20)_0%,transparent_60%)] pointer-events-none" />
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 lg:px-10 flex flex-col items-center text-center gap-8">
          <div className="flex items-center gap-3">
            <Flame className="text-[#ff6b1a]" size={20} />
            <span className="font-display uppercase tracking-[0.45em] text-[11px] text-[#ff6b1a]">
              Train hard. Run safe.
            </span>
            <Flame className="text-[#ff6b1a]" size={20} />
          </div>
          <h3 className="font-display uppercase text-4xl md:text-6xl font-black tracking-tight leading-[0.92] text-balance max-w-4xl">
            Ready to Give Your Crews
            <br />
            <span className="metallic-text">The Training They'll Actually Use?</span>
          </h3>
          <p className="text-zinc-300 text-base md:text-xl max-w-2xl leading-relaxed">
            OSHA 10, OSHA 30, stand-downs, foreman safety leadership —
            scoped to your operation, delivered where you run work, taught
            by someone who has actually run the work.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mt-2">
            <Link
              to="/contact?interest=Safety%20%26%20workforce%20training"
              data-testid="training-cta-primary"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-[#ff8533] transition-all shadow-[0_0_50px_rgba(255,107,26,0.4)] hover:shadow-[0_0_80px_rgba(255,107,26,0.6)]"
            >
              Schedule Onsite Training
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
            <Link
              to="/services"
              data-testid="training-cta-secondary"
              className="inline-flex items-center gap-3 px-8 py-4 border border-zinc-500 text-white font-display uppercase tracking-[0.18em] text-[13px] font-bold hover:bg-white/5 hover:border-[#ff6b1a] transition-all"
            >
              All Services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


